import React, { useState } from 'react';
import axios from 'axios';

const TeacherDeckSection = ({ classId, decks, onDeckUpdate }) => {
  const [selectedDeckTitle, setSelectedDeckTitle] = useState(null);
  const [activeDeckEvents, setActiveDeckEvents] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [modalContent, setModalContent] = useState('');

  const handleDeckClick = (deck) => {
    setSelectedDeckTitle(deck.title);
    setActiveDeckEvents(deck.events || []);
  };

  const handleRemoveFromDeck = async (deckTitle, eventId) => {
    if (!window.confirm('Remove this card from the deck?')) return;
    try {
      await axios.delete(
        `https://amora-backend.onrender.com/api/classes/${classId}/decks/${deckTitle}/events/${eventId}`
      );
      await onDeckUpdate(); // refresh after removal
    } catch (err) {
      console.error('Failed to remove from deck:', err);
      alert('⚠️ Failed to remove from deck.');
    }
  };

  const handleDeleteDeck = async (deckTitle) => {
    if (!window.confirm('Delete this entire deck?')) return;
    try {
      await axios.delete(`https://amora-backend.onrender.com/api/classes/${classId}/decks/${encodeURIComponent(deckTitle)}`);
      setSelectedDeckTitle(null);
      setActiveDeckEvents([]);
      await onDeckUpdate();
    } catch (err) {
      console.error('Failed to delete deck:', err);
      alert('⚠️ Failed to delete deck.');
    }
  };  

  const handleCardClick = async (event) => {
    try {
      setModalContent('⏳ Generating summary...');
      setOpenModal(true);
      const res = await axios.post('https://amora-backend.onrender.com/api/generate-summary', {
        prompt: `Summarize this historical event: ${event.title}, ${event.year}, ${event.description}`,
      });
      setModalContent(res.data.summary);
    } catch (err) {
      console.error('Failed to generate summary:', err);
      setModalContent('❌ Error generating summary.');
    }
  };

  return (
    <div className="mt-10 font-[Inter] text-white">
      <h2 className="text-2xl font-semibold text-purple-400 mb-6">📚 Decks</h2>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {decks.map((deck) => (
          <div
            key={deck.title}
            className="relative bg-gradient-to-br from-purple-700 to-indigo-800 p-5 rounded-xl shadow-md transform hover:scale-[1.02] transition cursor-pointer"
            onClick={() => handleDeckClick(deck)}
          >
            {/* Stack illusion */}
            <div className="absolute -top-2 -left-2 w-full h-full bg-purple-600 opacity-40 rounded-xl transform rotate-1 z-0"></div>
            <div className="absolute -top-4 -left-4 w-full h-full bg-purple-500 opacity-30 rounded-xl transform rotate-2 z-0"></div>

            <div className="relative z-10">
              <h3 className="text-xl font-bold mb-1">{deck.title}</h3>
              <p className="text-sm text-gray-200">{deck.events?.length || 0} cards</p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteDeck(deck.title);
                }}
                className="absolute top-2 right-2 text-sm text-red-300 hover:text-red-500"
              >
                ❌
              </button>
            </div>
          </div>
        ))}
      </div>

      {selectedDeckTitle && (
        <div className="mt-8 bg-gray-900 p-6 rounded-xl shadow-lg border border-gray-700">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-purple-300">{selectedDeckTitle} - Cards</h3>
            <button
              onClick={() => {
                setSelectedDeckTitle(null);
                setActiveDeckEvents([]);
              }}
              className="text-blue-400 hover:underline"
            >
              Close Deck
            </button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {activeDeckEvents.map((event) => (
              <div
                key={event._id}
                className="bg-gray-800 border border-gray-700 p-4 rounded-xl shadow hover:shadow-md transition relative cursor-pointer"
                onClick={() => handleCardClick(event)}
              >
                <h4 className="text-lg font-semibold text-purple-200">{event.title}</h4>
                <p className="text-sm text-gray-400 mb-1">📅 {event.year}</p>
                <p className="text-sm text-gray-400 mb-2">🏷 {event.category}</p>
                <p className="text-sm text-gray-300">{event.description}</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFromDeck(selectedDeckTitle, event._id);
                  }}
                  className="absolute top-2 right-2 text-red-400 hover:text-red-600 text-sm"
                >
                  ❌
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {openModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
          <div className="bg-gray-900 text-white p-6 rounded-lg shadow-lg max-w-lg w-full relative max-h-[80vh] overflow-y-auto">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-100"
              onClick={() => setOpenModal(false)}
            >
              ✖
            </button>
            <h2 className="text-xl font-bold mb-2">🧠 AI Summary</h2>
            <p className="text-gray-200 whitespace-pre-line">{modalContent}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherDeckSection;
